import {
  type ConsentChoice,
  type ConsentEventPayload,
  type ConsentEventType,
  type SiteConfig,
  DEFAULT_I18N,
  isConsentRequiredRegion,
} from "@cmp/shared";

import { logEvent } from "./api.js";
import {
  acceptAllChoice,
  buildProofSnapshot,
  declineAllChoice,
  fullyGrantedChoice,
  renderBanner,
  renderPill,
  renderPreferences,
} from "./banner.js";
import { fetchConfig } from "./config.js";
import { dispatchChange, dispatchReady, setDefaultsDenied, updateConsent } from "./consent.js";
import { waitForBody } from "./dom.js";
import { clearStored, readStored, writeStored, type StoredConsent } from "./storage.js";

declare const __CMP_VERSION__: string;
declare const __CMP_STYLES__: string;

interface LoaderOptions {
  siteId: string;
  apiBase: string;
}

function readLoaderOptions(): LoaderOptions | null {
  const script = document.currentScript as HTMLScriptElement | null;
  const fallback = document.querySelector<HTMLScriptElement>("script[data-site-id]");
  const tag = script?.dataset["siteId"] ? script : fallback;
  if (!tag) return null;
  const siteId = tag.dataset["siteId"];
  if (!siteId) return null;
  const explicit = tag.dataset["apiBase"];
  const apiBase = explicit ?? deriveApiBase(tag.src);
  return { siteId, apiBase };
}

function deriveApiBase(scriptSrc: string): string {
  try {
    const u = new URL(scriptSrc);
    return `${u.protocol}//api.${u.host.replace(/^consent\./, "")}`;
  } catch {
    return "/consent-api";
  }
}

function pickLocale(config: SiteConfig): string {
  const htmlLang = document.documentElement.lang?.toLowerCase().split("-")[0];
  if (htmlLang && config.i18n[htmlLang]) return htmlLang;
  if (config.i18n["en"]) return "en";
  const first = Object.keys(config.i18n)[0];
  return first ?? "en";
}

function buildEventPayload(args: {
  config: SiteConfig;
  stored: StoredConsent;
  type: ConsentEventType;
  proofText: string;
}): ConsentEventPayload {
  return {
    site_id: args.config.site_id,
    consent_id: args.stored.consent_id,
    event_type: args.type,
    choice: args.stored.choice,
    page_url: location.href,
    banner_version: args.config.banner_version,
    policy_version: args.config.policy_version,
    proof_text_snapshot: args.proofText,
    client_ts: new Date().toISOString(),
  };
}

function uuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function boot(): Promise<void> {
  // Step 1: defaults denied — must happen before any tag loads.
  setDefaultsDenied();

  const opts = readLoaderOptions();
  if (!opts) {
    console.error("[cmp] missing data-site-id on loader script");
    return;
  }

  const config = await fetchConfig(opts.apiBase, opts.siteId);
  await waitForBody();
  const locale = pickLocale(config);
  const i18n = config.i18n[locale] ?? DEFAULT_I18N["en"]!;
  const proofText = buildProofSnapshot(i18n);

  const regionRequires =
    config.region_mode === "global" || isConsentRequiredRegion(config.detected_region);

  const commit = (choice: ConsentChoice, type: ConsentEventType): StoredConsent => {
    const prior = readStored();
    const stored: StoredConsent = {
      consent_id: prior?.consent_id ?? uuid(),
      choice,
      policy_version: config.policy_version,
      banner_version: config.banner_version,
      ts: new Date().toISOString(),
    };
    writeStored(stored);
    updateConsent(choice);
    dispatchChange(choice);
    logEvent(opts.apiBase, buildEventPayload({ config, stored, type, proofText }));
    return stored;
  };

  const showPill = (): void => {
    renderPill({
      label: i18n.pill_label,
      stylesCss: __CMP_STYLES__,
      accent: config.accent_color,
      onClick: () => openPrefs(readStored()?.choice ?? fullyGrantedChoice()),
    });
  };

  const isFullAccept = (choice: ConsentChoice): boolean => {
    const t = acceptAllChoice(config);
    return (
      choice.security_storage === t.security_storage &&
      choice.analytics_storage === t.analytics_storage &&
      choice.functionality_storage === t.functionality_storage &&
      choice.personalization_storage === t.personalization_storage &&
      choice.ad_storage === t.ad_storage &&
      choice.ad_user_data === t.ad_user_data &&
      choice.ad_personalization === t.ad_personalization
    );
  };

  const maybeShowPill = (choice: ConsentChoice): void => {
    if (isFullAccept(choice)) return;
    showPill();
  };

  const closeAnyBanner = (): void => {
    document.querySelector<HTMLElement>("[data-cmp-banner-bar]")?.remove();
    document.querySelector<HTMLElement>("[data-cmp-banner-dim]")?.remove();
  };

  const openPrefs = (initial: ConsentChoice): void => {
    renderPreferences({
      config,
      i18n,
      stylesCss: __CMP_STYLES__,
      initial,
      callbacks: {
        onAcceptAll: () => {
          commit(acceptAllChoice(config), "accept_all");
          dispatchReady(readStored()!.choice);
          closeAnyBanner();
          maybeShowPill(readStored()!.choice);
        },
        onDeclineAll: () => {
          commit(declineAllChoice(), "reject_all");
          dispatchReady(readStored()!.choice);
          closeAnyBanner();
          maybeShowPill(readStored()!.choice);
        },
        onConfirmChoices: (choice) => {
          commit(choice, "save_preferences");
          dispatchReady(readStored()!.choice);
          closeAnyBanner();
          maybeShowPill(readStored()!.choice);
        },
      },
    });
  };

  const showBanner = (): void => {
    renderBanner({
      config,
      i18n,
      stylesCss: __CMP_STYLES__,
      callbacks: {
        onAcceptAll: () => {
          commit(acceptAllChoice(config), "accept_all");
          dispatchReady(readStored()!.choice);
          maybeShowPill(readStored()!.choice);
        },
        onDeclineAll: () => {
          commit(declineAllChoice(), "reject_all");
          dispatchReady(readStored()!.choice);
          maybeShowPill(readStored()!.choice);
        },
        onOpenPreferences: () => openPrefs(readStored()?.choice ?? fullyGrantedChoice()),
      },
    });
  };

  const prior = readStored();

  if (prior && prior.policy_version === config.policy_version) {
    updateConsent(prior.choice);
    logEvent(opts.apiBase, buildEventPayload({ config, stored: prior, type: "replay", proofText }));
    dispatchReady(prior.choice);
    maybeShowPill(prior.choice);
  } else if (prior && prior.policy_version !== config.policy_version) {
    clearStored();
    if (regionRequires) {
      showBanner();
    } else {
      dispatchReady(declineAllChoice());
      maybeShowPill(declineAllChoice());
    }
  } else if (regionRequires) {
    showBanner();
  } else {
    dispatchReady(declineAllChoice());
    maybeShowPill(declineAllChoice());
  }

  // Public API
  window.__cmp = {
    version: __CMP_VERSION__,
    openPreferences: () => openPrefs(readStored()?.choice ?? fullyGrantedChoice()),
    acceptAll: () => {
      commit(acceptAllChoice(config), "accept_all");
      dispatchReady(readStored()!.choice);
      maybeShowPill(readStored()!.choice);
    },
    rejectAll: () => {
      commit(declineAllChoice(), "reject_all");
      dispatchReady(readStored()!.choice);
      maybeShowPill(readStored()!.choice);
    },
    getChoice: () => readStored()?.choice ?? null,
    withdraw: () => {
      commit(declineAllChoice(), "withdraw");
      dispatchReady(readStored()!.choice);
      maybeShowPill(readStored()!.choice);
    },
  };
}

if (typeof window !== "undefined") {
  boot().catch((err: unknown) => {
    console.error("[cmp] boot failed", err);
  });
}
